import { Box, Stack, Typography } from '@mui/material';
import { round } from 'lodash';
import { Fragment, useMemo } from 'react';
import Icon from '/@/components/icon';
import { _ } from '/@/locale';
import type { ApplicationReport } from '/@/types/application-report';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { RegistryFooter } from './card-footer';
import { Cell, COLUMNS } from './data-grid';
import { RegistryCard } from './registry-card';
import RegistryNotes from './registry-notes';
import { TablaCard, TablaCardRow } from './tabla-card';

/**
 * Read-only timeline card for a FieldView application report. Same layout as the manual
 * application card, but flagged with the FieldView icon and without edit/delete options.
 */
const RegistryApplicationReport = ({
    report,
    notes,
    cropMonitoring,
    expanded,
    onToggle,
}: {
    report: ApplicationReport;
    notes: CropMonitoringNote[];
    /** The campaign's crop monitoring: gives the tracked events their crop and phenology. */
    cropMonitoring?: CropMonitoring;
    /** Mobile-only collapse state, injected by `Registries`' accordion. */
    expanded?: boolean;
    onToggle?: () => void;
}) => {
    // Neither the API nor the model orders results, so sort here: a card's rows can span
    // several days and the first column is the date. Dates are ISO (YYYY-MM-DD), so a
    // lexical compare is chronological. Copied because results is a prop.
    const sortedResults = useMemo(
        () => [...report.results].sort((a, b) => a.eventStartDate.localeCompare(b.eventStartDate)),
        [report.results]
    );
    const first = sortedResults[0];
    if (!first) return null;

    return (
        <RegistryCard
            header={
                <>
                    <Icon type='fieldview' style={{ fill: '#F7B618', width: '20px', height: '20px' }} />
                    <Typography variant='h6' color='primary'>
                        {`${_('Aplicación de')} ${first.applicationName}`}
                    </Typography>
                </>
            }
            footer={<RegistryFooter authorName='FieldView' date={formatDate(new Date(report.created))} />}
            expanded={expanded}
            onToggle={onToggle}
        >
            {onToggle ? (
                <Stack spacing={1} sx={{ width: '100%' }}>
                    {sortedResults.map((result) => (
                        <TablaCard key={result.id}>
                            <TablaCardRow
                                label={_('Fecha de aplicación')}
                                value={formatDate(new Date(result.eventStartDate))}
                            />
                            <TablaCardRow label={_('Producto')} value={result.chemicalProductName} />
                            <TablaCardRow
                                label={_('Dosis')}
                                value={`${round(result.averageApplicationRate, 2)} ${result.applicationRateUnits}`.trim()}
                            />
                        </TablaCard>
                    ))}
                </Stack>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, width: '100%', overflow: 'auto' }}>
                    <Cell header>{_('Fecha de aplicación')}</Cell>
                    <Cell header>{_('Producto')}</Cell>
                    <Cell header>{_('Dosis')}</Cell>
                    {sortedResults.map((result) => (
                        <Fragment key={result.id}>
                            <Cell>{formatDate(new Date(result.eventStartDate))}</Cell>
                            <Cell>{result.chemicalProductName}</Cell>
                            <Cell>
                                {`${round(result.averageApplicationRate, 2)} ${result.applicationRateUnits}`.trim()}
                            </Cell>
                        </Fragment>
                    ))}
                </Box>
            )}

            <RegistryNotes
                notes={notes}
                fieldId={report.field}
                applicationReportResultId={first.id}
                cropMonitoring={cropMonitoring}
                registryType='application'
                registryOrigin='fieldview'
            />
        </RegistryCard>
    );
};

export default RegistryApplicationReport;
